import React, { useEffect, useState, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, Coins, Wallet, Sparkles, Shield, Rocket, Loader2, Info } from "lucide-react";
import { apiPost, apiGet, formatLKR, formatPct } from "@/lib/api";

const SCENARIO_META = {
  conservative: {
    label: "Conservative",
    icon: Shield,
    accentClass: "text-[hsl(var(--muted-foreground))]",
    bgClass: "bg-muted/40",
    borderClass: "border-muted-foreground/30",
    blurb: "Flat dividend growth (0%) + 2% capital appreciation",
  },
  base_case: {
    label: "Base Case",
    icon: TrendingUp,
    accentClass: "text-[hsl(var(--primary))]",
    bgClass: "bg-primary/5",
    borderClass: "border-primary/30",
    blurb: "Historical DPS growth + sector-PE convergence",
  },
  optimistic: {
    label: "Optimistic",
    icon: Rocket,
    accentClass: "text-[hsl(var(--success))]",
    bgClass: "bg-[hsl(var(--success))]/5",
    borderClass: "border-[hsl(var(--success))]/30",
    blurb: "1.5× DPS growth + Graham Number convergence",
  },
};

const ROWS = [
  // ── Dividend Income block ──────────────────────────────────────────
  {
    key: "_section_dividend",
    section: "Dividend Income",
  },
  {
    key: "annual_dividend_income",
    label: "Year 1 Annual Dividend",
    icon: Coins,
    format: "lkr",
  },
  {
    key: "annual_dividend_income_year_n",
    label: (years) => `Year ${years} Annual Dividend`,
    icon: Coins,
    format: "lkr",
  },
  {
    key: "total_dividends_period",
    label: (years) => `${years}-Year Total Dividends`,
    icon: Coins,
    format: "lkr",
  },
  // ── Capital Growth block ───────────────────────────────────────────
  // Phase 9 Fix 6 — surface capital growth explicitly so the projection
  // never looks like it's only forecasting dividend income.
  {
    key: "_section_capital",
    section: "Capital Growth",
  },
  {
    key: "_entry_value",
    label: "Entry value (initial investment)",
    icon: Wallet,
    format: "lkr",
  },
  {
    key: "capital_value_at_end",
    label: (years) => `Exit value at Year ${years}`,
    icon: Wallet,
    format: "lkr",
  },
  {
    key: "_capital_gain",
    label: (years) => `Capital gain over ${years} years`,
    icon: TrendingUp,
    format: "lkr_pct",
    pctKey: "_capital_gain_pct",
  },
  // ── Total ──────────────────────────────────────────────────────────
  {
    key: "_section_total",
    section: "TOTAL Return (Dividends + Capital)",
  },
  {
    key: "total_return",
    label: "Total return (LKR)",
    icon: TrendingUp,
    format: "lkr_pct",
    pctKey: "total_return_pct",
  },
  {
    key: "annualised_return_pct",
    label: "Annualised return",
    icon: TrendingUp,
    format: "pct",
  },
];

/**
 * ReturnProjectionPanel — Phase 7 Stage 7.
 *
 * Two modes:
 *   1. portfolioId given  → fetches /api/portfolios/{id}/projection
 *      and persists settings via /projection/settings.
 *   2. suggestion given   → preview-only via /api/portfolios/projection/preview
 *      (used in the creation flow before the portfolio is saved).
 */
const ReturnProjectionPanel = ({
  portfolioId = null,
  suggestion = null,
  initialReinvest = false,
  initialYears = 5,
  initialApplyWht = true,
  testIdPrefix = "projection",
}) => {
  const [reinvest, setReinvest] = useState(initialReinvest);
  const [years, setYears] = useState(initialYears);
  // Apply 10% Sri Lanka dividend withholding tax by default (matches
  // statutory deduction at source). Users with tax-exempt portfolios
  // can toggle this OFF per portfolio — the preference is persisted in
  // portfolios.projection_settings.apply_dividend_wht.
  const [applyWht, setApplyWht] = useState(initialApplyWht);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let res;
      if (portfolioId) {
        res = await apiGet(
          `/portfolios/${portfolioId}/projection?years=${years}&reinvest=${reinvest}&apply_wht=${applyWht}`
        );
        // Best effort: persist preference (don't block render)
        apiPost(`/portfolios/${portfolioId}/projection/settings`, {
          reinvest_dividends: reinvest,
          holding_period_years: years,
          apply_dividend_wht: applyWht,
        }).catch(() => {});
      } else if (suggestion) {
        res = await apiPost(`/portfolios/projection/preview`, {
          suggestion,
          years,
          reinvest_dividends: reinvest,
          apply_dividend_wht: applyWht,
        });
      }
      setData(res || null);
      // Hydrate the switch from the server's effective value on first
      // load so a portfolio previously opted-out stays opted-out after
      // navigating away and back.
      if (
        res
        && typeof res.apply_dividend_wht === "boolean"
        && res.apply_dividend_wht !== applyWht
      ) {
        setApplyWht(res.apply_dividend_wht);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Could not load projection");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portfolioId, suggestion, years, reinvest, applyWht]);

  useEffect(() => {
    load();
  }, [load]);

  const renderCell = (row, scenarioKey) => {
    const sc = data?.[scenarioKey];
    if (!sc) return <span className="text-muted-foreground">—</span>;
    // Phase 9 Fix 6 — derived rows
    if (row.key === "_entry_value") {
      const entry =
        data?.initial_investment ??
        data?.investment_amount ??
        data?.summary?.investment ??
        sc?.initial_investment;
      if (entry == null) return <span className="text-muted-foreground">—</span>;
      return formatLKR(entry, { compact: true, decimals: 1 });
    }
    if (row.key === "_capital_gain") {
      const entry =
        data?.initial_investment ??
        data?.investment_amount ??
        data?.summary?.investment ??
        sc?.initial_investment ??
        0;
      const exit = sc?.capital_value_at_end;
      if (entry == null || exit == null) return <span className="text-muted-foreground">—</span>;
      const gain = exit - entry;
      const pct = entry > 0 ? (gain / entry) * 100 : null;
      const tone = gain >= 0 ? "text-[hsl(var(--success))]" : "text-[hsl(var(--destructive))]";
      return (
        <span className={tone}>
          {gain >= 0 ? "+" : ""}
          {formatLKR(gain, { compact: true, decimals: 1 })}
          {pct != null && (
            <span className="ml-1 text-xs text-muted-foreground">
              ({pct >= 0 ? "+" : ""}{pct.toFixed(1)}%)
            </span>
          )}
        </span>
      );
    }
    const v = sc[row.key];
    if (v == null) return <span className="text-muted-foreground">—</span>;
    if (row.format === "lkr") return formatLKR(v, { compact: true, decimals: 1 });
    if (row.format === "pct") return formatPct(v);
    if (row.format === "lkr_pct") {
      const p = sc[row.pctKey];
      return (
        <span>
          {formatLKR(v, { compact: true, decimals: 1 })}
          {p != null && (
            <span className="ml-1 text-xs text-muted-foreground">
              ({formatPct(p)})
            </span>
          )}
        </span>
      );
    }
    return String(v);
  };

  return (
    <Card className="border bg-card" data-testid={`${testIdPrefix}-panel`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="font-display text-base flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-[hsl(var(--primary))]" />
              📈 Projected Returns
              {suggestion && !portfolioId && (
                <Badge variant="secondary" className="text-[10px]">
                  based on suggested allocation
                </Badge>
              )}
            </CardTitle>
            <CardDescription>
              Three-scenario {years}-year projection — dividend income + capital appreciation.
            </CardDescription>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Label htmlFor={`${testIdPrefix}-reinvest`} className="text-xs cursor-pointer">
                💰 Reinvest dividends
              </Label>
              <Switch
                id={`${testIdPrefix}-reinvest`}
                checked={reinvest}
                onCheckedChange={setReinvest}
                data-testid={`${testIdPrefix}-reinvest-toggle`}
              />
            </div>
            <div
              className="flex items-center gap-2"
              title="Sri Lanka deducts 10% withholding tax at source on dividends. Turn OFF for tax-exempt accounts (some funds, non-resident DTAA cases)."
            >
              <Label htmlFor={`${testIdPrefix}-apply-wht`} className="text-xs cursor-pointer">
                🏛️ Apply 10% Dividend WHT
              </Label>
              <Switch
                id={`${testIdPrefix}-apply-wht`}
                checked={applyWht}
                onCheckedChange={setApplyWht}
                data-testid={`${testIdPrefix}-apply-wht-toggle`}
              />
            </div>
            <select
              value={years}
              onChange={(e) => setYears(parseInt(e.target.value, 10))}
              className="h-8 rounded-md border bg-background px-2 text-xs focus:outline-none focus:ring-2 focus:ring-primary"
              data-testid={`${testIdPrefix}-years-select`}
            >
              {[3, 5, 7, 10].map((y) => (
                <option key={y} value={y}>
                  {y} years
                </option>
              ))}
            </select>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading && !data && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-6">
            <Loader2 className="h-4 w-4 animate-spin" /> Calculating projection…
          </div>
        )}
        {error && (
          <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
            {error}
          </div>
        )}
        {data && (
          <>
            {/* Scenario header strip */}
            <div className="grid grid-cols-3 gap-3" data-testid={`${testIdPrefix}-headers`}>
              {Object.entries(SCENARIO_META).map(([key, meta]) => {
                const Icon = meta.icon;
                return (
                  <div
                    key={key}
                    className={`rounded-md border ${meta.borderClass} ${meta.bgClass} p-2.5`}
                  >
                    <div className={`flex items-center gap-1.5 text-xs font-display font-semibold ${meta.accentClass}`}>
                      <Icon className="h-3.5 w-3.5" /> {meta.label}
                    </div>
                    <div className="text-[10px] text-muted-foreground mt-0.5 leading-snug">
                      {meta.blurb}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Comparison table */}
            <div className="overflow-x-auto rounded-md border" data-testid={`${testIdPrefix}-table`}>
              <table className="w-full text-sm">
                <thead className="bg-muted/30 border-b text-[11px] uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 text-left">Metric</th>
                    {Object.entries(SCENARIO_META).map(([key, meta]) => (
                      <th key={key} className={`px-3 py-2 text-right ${meta.accentClass}`}>
                        {meta.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {ROWS.map((row) => {
                    // Phase 9 Fix 6 — Section divider rows
                    if (row.section) {
                      return (
                        <tr key={row.key} className="bg-muted/40 border-b">
                          <td
                            colSpan={1 + Object.keys(SCENARIO_META).length}
                            className="px-3 py-1.5 text-[10px] uppercase tracking-wider font-semibold text-muted-foreground"
                          >
                            {row.section}
                          </td>
                        </tr>
                      );
                    }
                    const Icon = row.icon;
                    const label =
                      typeof row.label === "function" ? row.label(years) : row.label;
                    return (
                      <tr key={row.key} className="border-b last:border-0">
                        <td className="px-3 py-2.5 text-muted-foreground">
                          <span className="inline-flex items-center gap-1.5">
                            <Icon className="h-3.5 w-3.5" />
                            {label}
                          </span>
                        </td>
                        {Object.keys(SCENARIO_META).map((scKey) => (
                          <td
                            key={scKey}
                            className="px-3 py-2.5 text-right tabular font-medium"
                            data-testid={`${testIdPrefix}-${scKey}-${row.key}`}
                          >
                            {renderCell(row, scKey)}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Stage 1 — Brokerage charges row */}
            {data.brokerage && data.scenarios_inc_brokerage && (
              <div
                className="overflow-x-auto rounded-md border bg-muted/20"
                data-testid={`${testIdPrefix}-brokerage-table`}
              >
                <table className="w-full text-sm">
                  <thead className="bg-muted/40 border-b text-[11px] uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2 text-left">
                        {applyWht ? "After Brokerage & Dividend WHT" : "After Brokerage (WHT exempt)"}
                      </th>
                      {Object.entries(SCENARIO_META).map(([key, meta]) => (
                        <th key={key} className={`px-3 py-2 text-right ${meta.accentClass}`}>
                          {meta.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b">
                      <td className="px-3 py-2 text-muted-foreground">
                        Buy brokerage (paid)
                      </td>
                      {Object.keys(SCENARIO_META).map((scKey) => (
                        <td key={scKey} className="px-3 py-2 text-right tabular text-[hsl(var(--destructive))]">
                          −{formatLKR(data.brokerage.total_buy_brokerage, { compact: true, decimals: 1 })}
                        </td>
                      ))}
                    </tr>
                    <tr className="border-b">
                      <td className="px-3 py-2 text-muted-foreground">
                        Sell brokerage (estimated at year {years})
                      </td>
                      {Object.keys(SCENARIO_META).map((scKey) => {
                        const v = data.scenarios_inc_brokerage[scKey]?.sell_brokerage_estimate;
                        return (
                          <td key={scKey} className="px-3 py-2 text-right tabular text-[hsl(var(--destructive))]">
                            −{formatLKR(v, { compact: true, decimals: 1 })}
                          </td>
                        );
                      })}
                    </tr>
                    <tr className="border-b">
                      <td className="px-3 py-2 text-muted-foreground">
                        {applyWht
                          ? "Dividend WHT (10% withheld at source)"
                          : "Dividend WHT — disabled for this portfolio"}
                      </td>
                      {Object.keys(SCENARIO_META).map((scKey) => {
                        const v = data.scenarios_inc_brokerage[scKey]?.total_dividend_wht;
                        const displayV = applyWht ? v : 0;
                        return (
                          <td
                            key={scKey}
                            className={`px-3 py-2 text-right tabular ${applyWht ? "text-[hsl(var(--destructive))]" : "text-muted-foreground"}`}
                          >
                            {applyWht ? "−" : ""}{formatLKR(displayV, { compact: true, decimals: 1 })}
                          </td>
                        );
                      })}
                    </tr>
                    <tr className="border-b bg-card font-medium">
                      <td className="px-3 py-2.5">
                        Net total return (after charges)
                      </td>
                      {Object.keys(SCENARIO_META).map((scKey) => {
                        const sc = data.scenarios_inc_brokerage[scKey] || {};
                        const v = sc.net_total_return_lkr;
                        const p = sc.net_total_return_pct;
                        const positive = (v || 0) >= 0;
                        return (
                          <td
                            key={scKey}
                            className={`px-3 py-2.5 text-right tabular font-semibold ${positive ? "text-[hsl(var(--success))]" : "text-[hsl(var(--destructive))]"}`}
                            data-testid={`${testIdPrefix}-${scKey}-net-return`}
                          >
                            {positive ? "+" : ""}{formatLKR(v, { compact: true, decimals: 1 })}
                            {p != null && (
                              <span className="ml-1 text-xs font-normal opacity-80">
                                ({formatPct(p)})
                              </span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                    <tr>
                      <td className="px-3 py-2 text-muted-foreground">
                        Net annualised return
                      </td>
                      {Object.keys(SCENARIO_META).map((scKey) => {
                        const v = data.scenarios_inc_brokerage[scKey]?.net_annualised_return_pct;
                        return (
                          <td key={scKey} className="px-3 py-2 text-right tabular">
                            {v != null ? formatPct(v) : "—"}
                          </td>
                        );
                      })}
                    </tr>
                  </tbody>
                </table>
                <div className="px-3 py-2 text-[11px] text-muted-foreground border-t bg-muted/10 space-y-0.5">
                  <div>
                    Brokerage rates: <span className="font-medium text-foreground">
                      {(data.brokerage.buy_rate * 100).toFixed(2)}% buy
                    </span>{" / "}
                    <span className="font-medium text-foreground">
                      {(data.brokerage.sell_rate * 100).toFixed(2)}% sell
                    </span>
                    {" — "}
                    Total estimated charges (round-trip):{" "}
                    <span className="font-medium text-foreground tabular">
                      {formatLKR(
                        (data.brokerage.total_buy_brokerage || 0)
                        + (data.scenarios_inc_brokerage.base_case?.sell_brokerage_estimate || 0),
                        { compact: true, decimals: 1 }
                      )}
                    </span>
                  </div>
                  <div className="text-[hsl(var(--success))]">
                    ✓ No capital gains tax on profits from CSE listed shares
                    {applyWht
                      ? " · Dividend income shown net of 10% WHT"
                      : " · 10% Dividend WHT DISABLED for this portfolio (tax-exempt account)"}
                  </div>
                </div>
              </div>
            )}

            {/* Disclaimer */}
            <div className="flex items-start gap-2 rounded-md border border-warning/30 bg-warning/5 p-3 text-[11px] leading-snug text-muted-foreground">
              <Info className="h-3.5 w-3.5 mt-0.5 shrink-0 text-[hsl(var(--warning))]" />
              <p>
                These projections are estimates based on current yields and historical data.
                Actual returns will differ materially. Past performance does not guarantee
                future results. Capital growth assumes price convergence to sector-median PE
                (base) or Graham Number (optimistic) over the holding period.
              </p>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default ReturnProjectionPanel;
